import { requestConfirmation, confirmationOptions } from "../lib/confirmation";
/**
 * Mass Actions Dock Controller
 * ===========================
 * A controller that manages bulk actions on selected items with a sliding dock interface.
 *
 * Purpose:
 * --------
 * Provides a bottom-fixed UI element for performing bulk actions on selected items.
 * Shows/hides automatically based on selection state.
 *
 * Required Structure:
 * ------------------
 * ```html
 * <div data-controller="mass-action">
 *   <!-- Select All Checkbox -->
 *   <input data-mass-action-target="selectAll"
 *          data-action="mass-action#toggleAll"
 *          type="checkbox">
 *
 *   <!-- Individual Checkboxes -->
 *   <input data-mass-action-target="checkbox"
 *          data-record-id="<%= record.id %>"
 *          type="checkbox">
 *
 *   <!-- Dock Partial -->
 *   <%= render "shared/mass_actions_dock", actions: actions %>
 * </div>
 * ```
 *
 * Action Configuration:
 * -------------------
 * ```ruby
 * actions = [
 *   {
 *     label: "Action Name",      # Button text
 *     path: action_path,         # URL for action
 *     method: "POST",            # HTTP method
 *     confirm: "Confirm?",       # Confirmation message
 *     param_name: "ids[]",       # Parameter name for IDs
 *     extra_params: { },         # Additional parameters
 *     min_selected: 1,           # Minimum items required
 *     max_selected: 10,          # Maximum items allowed
 *     class: "btn-custom",       # Custom button classes
 *     data: { }                  # Additional data attributes
 *   }
 * ]
 * ```
 *
 * Data Targets:
 * ------------
 * - selectAll: Checkbox for selecting all items
 * - checkbox: Individual selectable items
 * - dock: The sliding dock element
 * - count: Element displaying selection count
 *
 * Actions:
 * -------
 * - toggleAll: Toggle all checkboxes
 * - toggleItem: Toggle single checkbox
 * - clearSelection: Clear all selections
 * - submitSelected: Process selected items
 * - appendIds: Add IDs to URL parameters
 *
 * Usage Examples:
 * -------------
 * 1. Direct Submit:
 *    ```ruby
 *    {
 *      label: "Update",
 *      path: "#",
 *      url: resource_path(':id'),
 *      method: "PATCH",
 *      param_name: "status"
 *    }
 *    ```
 *
 * 2. Append IDs (for forms/modals):
 *    ```ruby
 *    {
 *      label: "Merge",
 *      path: merge_form_path,
 *      data: {
 *        turbo_frame: "modal",
 *        action: "mass-action#appendIds"
 *      }
 *    }
 *    ```
 *
 * Best Practices:
 * -------------
 * 1. Always validate selections server-side
 * 2. Include confirmation for destructive actions
 * 3. Set appropriate min/max selection limits
 * 4. Handle mobile responsiveness
 * 5. Consider keyboard accessibility
 * 6. Manage CSRF tokens for submissions
 * 7. Handle error states appropriately
 */

import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";
import { i18n } from "../lib/i18n";

export default class extends Controller {
  static targets = [
    "selectAll",
    "checkbox",
    "dock",
    "count",
    "actionButton",
    "spinner",
  ];



  declare readonly hasSelectAllTarget: boolean;
  declare readonly selectAllTarget: HTMLInputElement;
  declare readonly checkboxTargets: HTMLInputElement[];
  declare readonly dockTarget: HTMLElement;
  declare readonly hasDockTarget: boolean;
  declare readonly countTarget: HTMLElement;
  declare readonly hasCountTarget: boolean;
  declare readonly actionButtonTargets: HTMLElement[];
  declare readonly spinnerTargets: HTMLElement[];
  private lastCheckedCheckbox: HTMLInputElement | null = null;

  connect() {
    this.updateDockState();
  }

  toggleAll(event: Event) {
    const checked = (event.target as HTMLInputElement).checked;
    this.checkboxTargets.forEach((checkbox) => {
      if (!checkbox.disabled) {
        checkbox.checked = checked;
      }
    });
    this.updateDockState();
    this.lastCheckedCheckbox = null;
  }

  // Check every loaded checkbox — the dock's "Select all" button, for lists
  // that have no header select-all checkbox.
  selectAllItems(event: Event) {
    event.preventDefault();
    this.checkboxTargets.forEach((checkbox) => {
      if (!checkbox.disabled) {
        checkbox.checked = true;
      }
    });
    if (this.hasSelectAllTarget) this.selectAllTarget.checked = true;
    this.updateDockState();
    this.lastCheckedCheckbox = null;
  }

  toggleItem(event: Event) {
    event.stopPropagation();
    const checkbox = event.target as HTMLInputElement;

    if (
      event instanceof MouseEvent &&
      event.shiftKey &&
      this.lastCheckedCheckbox &&
      checkbox.checked &&
      this.lastCheckedCheckbox.checked
    ) {
      // Handle shift-click selection. indexOf is -1 when the anchor's row was
      // replaced (Turbo) since the last click — fall back to a single toggle.
      const start = this.checkboxTargets.indexOf(this.lastCheckedCheckbox);
      const end = this.checkboxTargets.indexOf(checkbox);

      if (start !== -1 && end !== -1) {
        const [lower, upper] = start < end ? [start, end] : [end, start];

        // Set all checkboxes in range to checked state
        for (let i = lower; i <= upper; i++) {
          if (!this.checkboxTargets[i].disabled) {
            this.checkboxTargets[i].checked = true;
          }
        }
      }
    }

    // Only update lastCheckedCheckbox if the current checkbox is checked
    if (checkbox.checked) {
      this.lastCheckedCheckbox = checkbox;
    } else {
      // If unchecking, clear the last checked reference
      this.lastCheckedCheckbox = null;
    }

    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = this.checkboxTargets.every(
        (checkbox) => checkbox.checked,
      );
    }
    this.updateDockState();
  }

  clearSelection(event: Event) {
    event.preventDefault();
    if (this.hasSelectAllTarget) this.selectAllTarget.checked = false;
    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = false;
    });
    this.updateDockState();
    this.lastCheckedCheckbox = null;
  }

  updateDockState() {
    const checkedCount = this.checkboxTargets.filter(
      (checkbox) => checkbox.checked,
    ).length;
    setTimeout(() => {
      if (this.hasCountTarget)
        this.countTarget.textContent = checkedCount.toString();
    }, 100);

    if (!this.hasDockTarget) return;

    if (checkedCount > 0) {
      this.dockTarget.classList.remove("translate-y-full");
    } else {
      this.dockTarget.classList.add("translate-y-full");
    }
  }

  getSelectedIds(): string[] {
    return this.checkboxTargets
      .filter((checkbox) => checkbox.checked)
      .map((checkbox) => checkbox.dataset.recordId || "")
      .filter((id) => id !== "");
  }

  validateSelection(event: Event) {
    const element = event.currentTarget as HTMLElement;
    const selectedIds = this.getSelectedIds();
    const minSelected = parseInt(element.dataset.massActionMinSelected || "1");
    const maxSelected = parseInt(
      element.dataset.massActionMaxSelected || "999999",
    );

    if (selectedIds.length < minSelected) {
      event.preventDefault();
      alert(i18n.t('mass_action.select_at_least', { count: minSelected }));
      return false;
    }

    if (selectedIds.length > maxSelected) {
      event.preventDefault();
      alert(i18n.t('mass_action.select_at_most', { count: maxSelected }));
      return false;
    }

    return true;
  }

  appendIds(event: Event) {
    if (!this.validateSelection(event)) return;

    const link = event.currentTarget as HTMLAnchorElement;
    if (!link) return;

    const selectedIds = this.getSelectedIds();
    const url = new URL(link.href || window.location.href);
    const paramName = link.dataset.paramName || "ids[]";

    selectedIds.forEach((id) => {
      url.searchParams.append(paramName, id);
    });

    const frame = document.getElementById(
      link.dataset.turboFrame || "",
    ) as HTMLElement;
    if (frame) {
      event.preventDefault();
      frame.setAttribute("src", url.toString());
    }
  }

  setLoading(button: HTMLElement, isLoading: boolean) {
    const index = this.actionButtonTargets.indexOf(button);
    if (index === -1) return;

    const spinner = this.spinnerTargets[index];
    if (!spinner) return;
    if (isLoading) {
      button.setAttribute("disabled", "disabled");
      spinner.classList.remove("hidden");
    } else {
      button.removeAttribute("disabled");
      spinner.classList.add("hidden");
    }
  }

  async submitSelected(event: Event) {
    if (!this.validateSelection(event)) return;
    event.preventDefault();

    const button = event.currentTarget as HTMLButtonElement;
    if (!button) return;

    // Act on the rows the user confirmed, even if the list re-renders under the dialog.
    const selectedIds = this.getSelectedIds();
    if (button.dataset.confirm && !await requestConfirmation(button.dataset.confirm, confirmationOptions(button))) return;

    this.setLoading(button, true);

    const url = button.dataset.url || window.location.href;
    const method = button.dataset.method || "POST";
    const paramName = button.dataset.paramName || "ids";
    const redirectUrl = button.dataset.redirectUrl || window.location.href;

    try {
      if (button?.dataset?.bulkAction == "true") {
        const formData = new FormData();
        for (const id of selectedIds) {
          formData.append(paramName, id);
        }

        this.handleExtraParams(formData, button);
        await this.sendFetch(url, method, formData);
      } else {
        for (const id of selectedIds) {
          const formData = new FormData();
          formData.append(paramName, id);

          this.handleExtraParams(formData, button);
          // preserve backwards compatibility with :id param replacement
          await this.sendFetch(url.replace(":id", id), method, formData);
        }
      }

      // For bulk actions, let the server handle redirects via 302 responses
      // Only use Turbo.visit as fallback if no redirect occurred
      // if (!button?.dataset?.bulkAction) {
      Turbo.visit(redirectUrl, { action: "replace" });
      // }
    } catch (error) {
      console.error("Error processing mass action:", error);
      this.setLoading(button, false);
    }
  }

  handleExtraParams(formData: FormData, button: HTMLButtonElement) {
    if (button.dataset.extraParams) {
      const extraParams = JSON.parse(button.dataset.extraParams);
      Object.entries(extraParams).forEach(([key, value]) => {
        formData.append(key, value as string);
      });
    }
  }

  async sendFetch(url: string, method: string, formData: FormData) {
    await fetch(url, {
      method,
      headers: {
        "X-CSRF-Token":
          document
            .querySelector('meta[name="csrf-token"]')
            ?.getAttribute("content") || ""
      },
      body: formData
    });
  }
}
